import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  UnorderedList,
  ListItem,
  HStack,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { RiEyeLine } from 'react-icons/ri';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import {
  TableFilterOption,
  TableFilters,
} from '../../../../../../../components/TableFilters';
import { IUserListItem } from '../../../../../../../services/Users/ListUsersService';
import AccountStatusBadge from '../../../../components/AccountStatusBadge';
import FaceIdStatusBadge from '../../../../components/FaceIdStatusBadge';
import { VentureAssignmentActions } from '../../../../components/VentureAssignmentActions';

export interface IUser extends IUserListItem {
  formattedDate: string;
  formattedDocument?: string;
}

interface IUserTableProps extends BoxProps {
  users: IUser[];
  currentPage?: number;
  filterBy?: string;
  onChangeFilterBy?: (value: string) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt') => void;
  order?: 'ASC' | 'DESC';
  showVenturesColumn?: boolean;
  showDeleted?: boolean;
  onToggleShowDeleted?: () => void;
  onAssignmentChanged?: () => void;
  sortBy?: string;
  totalPages?: number;
}

export const UserTable = ({
  users,
  currentPage,
  filterBy,
  onChangeFilterBy,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  order,
  sortBy,
  totalPages,
  showVenturesColumn = false,
  showDeleted = false,
  onToggleShowDeleted,
  onAssignmentChanged,
  ...rest
}: IUserTableProps): JSX.Element => {
  const filterByOptions = useMemo<TableFilterOption[]>(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'Telefone',
        value: 'phone',
      },
      {
        label: 'CPF',
        value: 'cpf',
        maskType: 'cpf',
      },
    ],
    [],
  );

  const sortByOptions = useMemo<TableFilterOption[]>(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'Telefone',
        value: 'phone',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'CPF',
        value: 'cpf',
        maskType: 'cpf',
      },
      {
        label: 'Data de cadastro',
        value: 'createdAt',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <HStack align="flex-end" spacing="8">
        <TableFilters
          filterBy={filterBy}
          filterByOptions={filterByOptions}
          onChangeFilterBy={onChangeFilterBy}
          onFilter={onFilter}
          onOrder={onOrder}
          onSort={(sort) =>
            !!onSort &&
            onSort(sort as 'cpf' | 'name' | 'phone' | 'email' | 'createdAt')
          }
          order={order}
          sortBy={sortBy}
          sortByOptions={sortByOptions}
        />

        {!!onToggleShowDeleted && (
          <FormControl display="flex" alignItems="center" width="auto">
            <Switch
              id="show-deleted-filter"
              isChecked={showDeleted}
              onChange={() => onToggleShowDeleted()}
            />
            <FormLabel
              htmlFor="show-deleted-filter"
              mb="0"
              ml="2"
              whiteSpace="nowrap"
            >
              Removidos
            </FormLabel>
          </FormControl>
        )}
      </HStack>

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th w="8" textAlign="center">
              Status
            </Th>

            <Th>Funcionário</Th>

            <Th>Tipo</Th>

            {showVenturesColumn && <Th>Empreendimentos</Th>}

            <Th>Telefone</Th>

            <Th>E-mail</Th>

            <Th>CPF</Th>

            <Th textAlign="center">Data de cadastro</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {users.map((user) => (
            <Tr key={user.id}>
              <Td>
                <HStack justifyContent="space-between">
                  <Text>Face id:</Text>

                  <FaceIdStatusBadge
                    faceIdStatus={user.faceIdStatus}
                    avatarStatus={user.avatarStatus}
                  />
                </HStack>

                <HStack justifyContent="space-between">
                  <Text>Cadastro:</Text>

                  <AccountStatusBadge confirmPending={user.confirmPending} />
                </HStack>
              </Td>

              <Td>
                <Text fontWeight="bold">{user.name}</Text>
              </Td>

              <Td>{user.userAssignments[0]?.featureGroup?.name || '-'}</Td>

              {showVenturesColumn && (
                <Td>
                  <UnorderedList stylePosition="outside" m={0}>
                    {user.userAssignments.map((assignment) => (
                      <ListItem>
                        <Text>{assignment.venture?.name}</Text>
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Td>
              )}

              <Td>{user.phone || '-'}</Td>

              <Td>{user.email}</Td>

              <Td>{user.formattedDocument}</Td>

              <Td textAlign="center">{user.formattedDate}</Td>

              <Td textAlign="right">
                <HStack justifyContent="flex-end" spacing="2">
                  {!showDeleted && (
                    <LinkButton
                      px={0}
                      to={{
                        pathname: '/users/details',
                        state: {
                          userId: user.id,
                        },
                      }}
                    >
                      <Icon as={RiEyeLine} fontSize="16" />
                    </LinkButton>
                  )}

                  <VentureAssignmentActions
                    user={user}
                    onChanged={() => onAssignmentChanged?.()}
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
